import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { friendlyDeleteError } from '../utils/deleteError';
import { useDeleteConfirm } from '../hooks/useDeleteConfirm';
import { canDeleteForever } from '../utils/deleteGuard';
import AccountBar from './account/AccountBar';
import BreadcrumbBar, { type BreadcrumbAction } from './BreadcrumbBar';
import { useRole } from '../hooks/useRole';
import ArchivedToggle from './ui/ArchivedToggle';
import AccountMetrics from './account/AccountMetrics';
import { invoiceMetrics } from './account/accountMetricSets';
import { useExport } from './header/exporters';
import { isArchiveView, toArchiveView, type ArchiveView } from '../utils/archiveFilter';
import LoadErrorBox from './ui/LoadErrorBox';
import idColumn from './ui/idColumn';
import { useNavigate } from 'react-router-dom';
import Icon from './Icon';
import DataTable, { Column, actionsColumn } from './DataTable';
import ConfirmDialog from './ui/ConfirmDialog';
import EmptyState from './EmptyState';
import { useNotifications } from './NotificationProvider';
import { formatMoney } from '../utils/currency';
import { loadClients, Client } from '../services/ClientService';
import {
  loadClientInvoices,
  sendClientInvoice,
  voidClientInvoice,
  restoreClientInvoice,
  deleteClientInvoice,
  downloadClientInvoicePdf,
  ClientInvoice,
} from '../services/ClientInvoiceService';
import { NEW_INVOICE_PATH, clientInvoicePath, invoicePaymentPath } from '../utils/accountRoutes';
import { newBillingDocPath } from '../utils/projectBilling';
import '../styles/components/PipelineView.css';

// Fields a financially blind role must not be able to match through the header search.
export const INVOICE_MONEY_KEYS = ['subtotal', 'tax_rate', 'tax_amount', 'total', 'amount_paid', 'balance_due'] as const;

const STATUS_LABELS: Record<string, string> = {
  draft: 'Draft',
  sent: 'Sent',
  partial: 'Partial',
  paid: 'Paid',
  overdue: 'Overdue',
  void: 'Void',
};

// Account-level Client Invoices list — sits alongside Clients/Client Quotes
// in AccountShell. Same shape as ClientQuotesView.tsx (which itself mirrors
// ClientsView.tsx): toolbar-owning layout, Active/Archived/All filter,
// ConfirmDialog + DataTable actionsColumn (forms are their own pages).
interface ClientInvoicesViewProps {
  /** Show only this project's invoices, inside the project's Billing panel (no page header). */
  projectId?: string;
  /** The project's client, so New invoice starts with it chosen. */
  projectClientId?: string | null;
}

const ClientInvoicesView: React.FC<ClientInvoicesViewProps> = ({ projectId, projectClientId }) => {
  const embedded = !!projectId;
  const navigate = useNavigate();
  const { showError, showSuccess } = useNotifications();
  // Viewers read; money stays hidden from contractors and anyone flagged.
  const { canEdit, canViewFinancials } = useRole();

  const [invoices, setInvoices] = useState<ClientInvoice[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<unknown>(null);
  const [archiveFilter, setArchiveFilter] = useState<ArchiveView>('active');
  const [clientFilter, setClientFilter] = useState<string>('');

  const [archiveTarget, setArchiveTarget] = useState<ClientInvoice | null>(null);
  const [archiving, setArchiving] = useState(false);
  const [voidTarget, setVoidTarget] = useState<ClientInvoice | null>(null);
  const [voiding, setVoiding] = useState(false);
  const [sendingId, setSendingId] = useState<string | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const reload = useCallback(() => {
    setLoading(true);
    setLoadError(null);
    loadClientInvoices({ client_id: clientFilter || undefined, project_id: projectId || undefined, archived: archiveFilter })
      .then(setInvoices)
      // Shown in the page with a Retry, not as a toast that vanishes and
      // leaves what looks like an empty list.
      .catch(err => setLoadError(err ?? new Error('load failed')))
      .finally(() => setLoading(false));
  }, [archiveFilter, clientFilter, projectId, showError]);

  useEffect(() => { reload(); }, [reload]);

  useEffect(() => {
    loadClients('', 'active').then(setClients).catch(() => {});
  }, []);

  const clientName = (id: string) => clients.find(c => c.id === id)?.name ?? '—';
  // The Client column shows the name, so the header search has to see it on the row too.
  const rows = useMemo(
    () => invoices.map(inv => (inv.client_name ? inv : { ...inv, client_name: clients.find(c => c.id === inv.client_id)?.name })),
    [invoices, clients],
  );

  // Export button: the invoices listed right now; money columns only for a role that may see money.
  useExport('account-invoices', { rows: loading || loadError ? [] : rows, ctx: { showMoney: canViewFinancials } });

  const openNew = () => navigate(embedded
    ? newBillingDocPath(NEW_INVOICE_PATH, { projectId, clientId: projectClientId })
    : NEW_INVOICE_PATH);
  // Any invoice opens on its own page; only a draft is editable there, and
  // its payments are listed there as a section.
  const openInvoice = (inv: ClientInvoice) => navigate(clientInvoicePath(inv.id));
  const openPayment = (inv: ClientInvoice) => navigate(invoicePaymentPath(inv.id));

  const handleSend = async (inv: ClientInvoice) => {
    setSendingId(inv.id);
    try {
      await sendClientInvoice(inv.id);
      showSuccess(`Invoice ${inv.invoice_number} sent.`);
      reload();
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to send invoice.');
    } finally {
      setSendingId(null);
    }
  };

  const handleDownloadPdf = async (inv: ClientInvoice) => {
    setDownloadingId(inv.id);
    try {
      await downloadClientInvoicePdf(inv.id, inv.invoice_number);
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to download PDF.');
    } finally {
      setDownloadingId(null);
    }
  };

  const handleVoid = async () => {
    if (!voidTarget || voiding) return;
    setVoiding(true);
    try {
      await voidClientInvoice(voidTarget.id);
      showSuccess(`Invoice ${voidTarget.invoice_number} voided.`);
      setVoidTarget(null);
      reload();
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to void invoice.');
    } finally {
      setVoiding(false);
    }
  };

  const handleArchive = async () => {
    if (!archiveTarget || archiving) return;
    setArchiving(true);
    try {
      await deleteClientInvoice(archiveTarget.id);
      showSuccess(`Invoice ${archiveTarget.invoice_number} archived.`);
      setArchiveTarget(null);
      reload();
    } catch (err) {
      showError(friendlyDeleteError(err, { record: 'invoice', action: 'archive' }));
    } finally {
      setArchiving(false);
    }
  };

  const handleRestore = async (inv: ClientInvoice) => {
    try {
      await restoreClientInvoice(inv.id);
      showSuccess(`Invoice ${inv.invoice_number} restored.`);
      reload();
    } catch (err) {
      showError(friendlyDeleteError(err, { record: 'invoice', action: 'restore' }));
    }
  };

  // Delete forever is only offered on an archived record (the server refuses
  // otherwise); the dialog says why if it fails.
  const forever = useDeleteConfirm<ClientInvoice>({
    record: 'invoice',
    perform: row => deleteClientInvoice(row.id, true),
    onDone: () => { showSuccess('Invoice deleted.'); reload(); },
  });

  const columns: Column<ClientInvoice>[] = [
    idColumn<ClientInvoice>('clientInvoice'),
    { key: 'invoice_number', header: 'Invoice #', sortable: true },
    ...(embedded ? [] : [{ key: 'client_id', header: 'Client', render: (v: string, row: ClientInvoice) => row.client_name ?? clientName(v) }]),
    ...(canViewFinancials ? [
      { key: 'total', header: 'Total', align: 'right' as const, render: (v: number, row: ClientInvoice) => formatMoney(v, row.currency) },
      { key: 'amount_paid', header: 'Paid', align: 'right' as const, render: (v: number, row: ClientInvoice) => formatMoney(v, row.currency) },
      { key: 'balance_due', header: 'Balance', align: 'right' as const, render: (v: number, row: ClientInvoice) => formatMoney(v, row.currency) },
    ] : []),
    { key: 'status', header: 'Status', render: v => <span className={`status-pill status-${v}`}>{STATUS_LABELS[v] ?? v}</span> },
    actionsColumn<ClientInvoice>([
      { key: 'send', icon: 'send', title: 'Send', onClick: handleSend, show: row => canEdit && row.status === 'draft' && sendingId !== row.id },
      { key: 'record-payment', icon: 'dollar', title: 'Record Payment', onClick: openPayment, show: row => canEdit && ['sent', 'partial', 'overdue'].includes(row.status) },
      { key: 'view-payments', icon: 'list', title: 'View Payments', onClick: openInvoice, show: row => row.amount_paid > 0 },
      { key: 'download-pdf', icon: 'download', title: 'Download PDF', onClick: handleDownloadPdf, show: () => true },
      { key: 'edit', icon: 'edit', title: 'Edit', onClick: openInvoice, show: row => canEdit && row.status === 'draft' && !row.archived },
      { key: 'void', icon: 'x', title: 'Void', variant: 'danger', onClick: setVoidTarget, show: row => canEdit && !['void', 'paid'].includes(row.status) && !row.archived },
      { key: 'archive', icon: 'trash', title: 'Archive', variant: 'danger', onClick: setArchiveTarget, show: row => canEdit && row.status === 'draft' && !row.archived },
      { key: 'delete-forever', icon: 'trash', title: 'Delete forever', variant: 'danger', onClick: forever.request, show: row => canEdit && canDeleteForever(row) },
      { key: 'restore', icon: 'refresh', title: 'Restore', onClick: handleRestore, show: row => canEdit && !!row.archived },
    ]),
  ];

  // The page title is the shell's header title; New, the client filter and the
  // Archived switch sit in the header bar beside it, as on the project screens.
  const barActions: BreadcrumbAction[] = [
    ...(embedded ? [] : [{
      kind: 'filters' as const, key: 'filters',
      selects: [{
        key: 'client', label: 'Client', value: clientFilter, defaultValue: '', onChange: setClientFilter,
        options: [{ value: '', label: 'All clients' }, ...clients.map(c => ({ value: c.id, label: c.name }))],
      }],
    }]),
    ...(canEdit ? [{ kind: 'primary' as const, key: 'new', icon: 'plus', label: embedded ? 'New invoice' : 'New Invoice', onClick: openNew }] : []),
  ];

  return (
    <div className={embedded ? 'pb-list' : 'wt-container fade-in'}>
        {embedded ? (
          <div className="pb-toolbar">
            <ArchivedToggle checked={isArchiveView(archiveFilter)} onChange={on => setArchiveFilter(toArchiveView(on))} />
            <BreadcrumbBar actions={barActions} />
          </div>
        ) : (
          <AccountBar
            view="invoices"
            archived={{ checked: isArchiveView(archiveFilter), onChange: on => setArchiveFilter(toArchiveView(on)) }}
            actions={<BreadcrumbBar actions={barActions} />}
            exportKey="account-invoices"
          />
        )}
        {!embedded && archiveFilter !== 'archived' && !loadError && !loading && invoices.length > 0 && (
          <AccountMetrics label="Invoices summary" metrics={invoiceMetrics(invoices, canViewFinancials)} />
        )}
        {loadError ? (
          <LoadErrorBox what="invoices" error={loadError} onRetry={reload} retrying={loading} />
        ) : !loading && invoices.length === 0 ? (
          <EmptyState
            icon="creditCard"
            title={archiveFilter === 'archived' ? 'No archived invoices' : 'No invoices yet'}
            description={archiveFilter === 'archived'
              ? 'Invoices you archive will show up here.'
              : embedded
                ? 'Invoices for this project will show up here.'
                : 'Bill a client directly, or accept a client quote to generate one automatically.'}
            action={canEdit && archiveFilter !== 'archived' ? (
              <button type="button" className="pl-new-btn" onClick={openNew}>
                <Icon name="plus" size={14} /> {embedded ? 'New invoice' : 'Create first invoice'}
              </button>
            ) : undefined}
          />
        ) : (
          <DataTable<ClientInvoice>
            recordList={{ kind: 'clientInvoice' }}
            data={rows}
            columns={columns}
            loading={loading}
            filterOmit={canViewFinancials ? undefined : INVOICE_MONEY_KEYS}
            hoverable
            onRowClick={openInvoice}
            emptyMessage="No invoices match your search."
          />
        )}

      <ConfirmDialog
        {...forever.dialogProps}
        title="Delete this invoice forever?"
        subject={forever.target?.invoice_number}
        description="This cannot be undone."
        consequences={['The invoice and its line items', 'Its recorded payments']}
        confirmLabel="Delete forever"
      />
      <ConfirmDialog
        open={archiveTarget !== null}
        onClose={() => setArchiveTarget(null)}
        onConfirm={handleArchive}
        title={`Archive invoice ${archiveTarget?.invoice_number}?`}
        description="It will be hidden from the active list but not permanently deleted."
        confirmLabel="Archive"
        loading={archiving}
      />

      <ConfirmDialog
        open={voidTarget !== null}
        onClose={() => setVoidTarget(null)}
        onConfirm={handleVoid}
        title={`Void invoice ${voidTarget?.invoice_number}?`}
        description="Voided invoices no longer accept payments."
        confirmLabel="Void"
        loading={voiding}
      />
    </div>
  );
};

export default ClientInvoicesView;
